import { useEnhancedReducer } from 'hooks/useEnhancedReducer';
import { useRouter } from 'next/router';
import { createContext, Dispatch, FC, useContext, useEffect, useMemo } from 'react';
import { isUserStorageProvider } from 'services/userService';
import { update } from 'services/utilsService';
import { getUserAction, loginViaEmailAction } from 'state/actions/userActions';
import {
  ADD_EMAIL_FAILURE,
  ADD_EMAIL_REQUEST,
  ADD_EMAIL_SUCCESS,
  AUTHENTICATE_ADDRESS_FAILURE,
  AUTHENTICATE_ADDRESS_REQUEST,
  AUTHENTICATE_ADDRESS_SUCCESS,
  AUTHENTICATE_EMAIL_FAILURE,
  AUTHENTICATE_EMAIL_REQUEST,
  AUTHENTICATE_EMAIL_SUCCESS,
  AUTHENTICATE_LEDGER_FAILURE,
  AUTHENTICATE_LEDGER_REQUEST,
  AUTHENTICATE_LEDGER_SUCCESS,
  CLEAR_AUTHENTICATE,
  CLEAR_AUTHENTICATE_STATUS,
  CLEAR_UPDATE_USER,
  CLEAR_USER,
  GET_ALLOWANCES_FAILURE,
  GET_ALLOWANCES_REQUEST,
  GET_ALLOWANCES_SUCCESS,
  GET_ALLOWANCE_FAILURE,
  GET_ALLOWANCE_REQUEST,
  GET_ALLOWANCE_SUCCESS,
  GET_USER_FAILURE,
  GET_USER_REQUEST,
  GET_USER_SUCCESS,
  REQUEST_ALLOWANCE_FAILURE,
  REQUEST_ALLOWANCE_REQUEST,
  REQUEST_ALLOWANCE_SUCCESS,
  SET_IS_STORAGE_PROVIDER,
  UPDATE_USER_FAILURE,
  UPDATE_USER_REQUEST,
  UPDATE_USER_SUCCESS,
  WALLET_CHECK_SUPPORTED_FAILURE,
  WALLET_CHECK_SUPPORTED_REQUEST,
  WALLET_CHECK_SUPPORTED_SUCCESS,
} from 'state/actionTypes/userActionTypes';
import { WithContext } from 'types/context';
import { UserAllowances } from 'types/user';
import { useSegmentLoginTracking } from '../../../hooks/useSegmentLoginTracking';
import { UserAction, UserContextState, UserReducer } from './userContext.types';

type UserContext = {
  user: UserContextState;
  dispatch: Dispatch<UserAction>;
  getState: () => UserContextState;
};
type UseUserContext = () => UserContext;

const UserContext = createContext({} as UserContext);

const INITIAL_STATE: UserContextState = {
  userData: null,

  getUserRequest: { status: 'Initial' },
  updateUserRequest: { status: 'Initial' },

  getAllowancesRequest: { status: 'Initial' },
  requestAllowanceRequest: { status: 'Initial' },
  getAllowanceRequest: { status: 'Initial' },

  authenticateRequest: { status: 'Initial' },
  addEmailRequest: { status: 'Initial' },

  authenticatingWalletType: null,
  authenticatingSignData: null,

  authenticatingLedger: false,
  authenticatingLedgerError: '',
  // Loaded after authentication is complete
  walletAddress: null,
  walletType: null,

  supportedWallets: {},

  isStorageProvider: false,
};

const userReducer: UserReducer = (state, action) => {
  const { type } = action;

  switch (type) {
    case AUTHENTICATE_ADDRESS_REQUEST:
      return update(state, {
        authenticateRequest: { status: 'InProgress' },
        authenticatingWalletType: action.walletType,
        authenticatingSignData: action.signData,
      });

    case AUTHENTICATE_ADDRESS_SUCCESS:
      return update(state, {
        authenticateRequest: { status: 'Success' },
        authenticatingWalletType: null,
        authenticatingSignData: null,
        walletType: action.walletType,
        walletAddress: action.payload,
      });

    case AUTHENTICATE_ADDRESS_FAILURE:
      return update(state, {
        authenticateRequest: { status: 'Error', error: action.payload },
      });

    case AUTHENTICATE_EMAIL_REQUEST:
      return update(state, {
        authenticateRequest: { status: 'InProgress' },
      });

    case AUTHENTICATE_EMAIL_SUCCESS:
      return update(state, {
        authenticateRequest: { status: 'Success' },
      });

    case AUTHENTICATE_EMAIL_FAILURE:
      return update(state, {
        authenticateRequest: {
          status: 'Error',
          error: action.payload.message,
        },
      });

    case CLEAR_AUTHENTICATE:
      return ['InProgress', 'Success', 'Error'].includes(state.authenticateRequest.status)
        ? update(state, {
            authenticateRequest: { status: 'Initial' },
            authenticatingWalletType: null,
            authenticatingSignData: null,
          })
        : state;

    case GET_USER_REQUEST:
      return update(state, {
        getUserRequest: { status: 'InProgress' },
      });

    case GET_USER_SUCCESS: {
      const { user, walletType, walletAddress } = action.payload;

      return update(state, {
        getUserRequest: { status: 'Success' },
        userData: { ...user, allowances: {} },
        isStorageProvider: isUserStorageProvider({ ...user, allowances: {} }),
        walletType,
        walletAddress,
      });
    }

    case GET_USER_FAILURE:
      return update(state, {
        getUserRequest: { status: 'Error', error: action.payload },
        walletAddress: null,
        walletType: null,
        userData: null,
      });

    case CLEAR_USER: {
      return update(state, {
        ...INITIAL_STATE,
      });
    }

    case CLEAR_AUTHENTICATE_STATUS: {
      return update(state, {
        authenticateRequest: { status: 'Initial' },
      });
    }

    case GET_ALLOWANCES_REQUEST:
      return update(state, {
        getAllowancesRequest: { status: 'InProgress' },
      });

    case GET_ALLOWANCES_SUCCESS: {
      const { userData } = state;
      const { payload } = action;

      const allowances = payload.reduce<UserAllowances>((allowances, allowance) => {
        return { ...allowances, [allowance.minerId]: allowance };
      }, {});

      return update(state, {
        getAllowancesRequest: { status: 'Success' },
        userData: userData && { ...userData, allowances },
      });
    }

    case GET_ALLOWANCES_FAILURE:
      return update(state, {
        getAllowanceRequest: {
          status: 'Error',
          error: action.payload,
        },
      });

    case REQUEST_ALLOWANCE_REQUEST:
      return update(state, {
        requestAllowanceRequest: { status: 'InProgress' },
      });

    case REQUEST_ALLOWANCE_SUCCESS: {
      const { userData } = state;
      const { payload } = action;

      const allowances = userData
        ? { ...userData.allowances, [payload.minerId]: payload }
        : {};

      return update(state, {
        requestAllowanceRequest: { status: 'Success' },
        userData: userData && { ...userData, allowances },
      });
    }

    case REQUEST_ALLOWANCE_FAILURE:
      return update(state, {
        requestAllowanceRequest: {
          status: 'Error',
          error: action.payload,
        },
      });

    case GET_ALLOWANCE_REQUEST:
      return update(state, {
        getAllowanceRequest: { status: 'InProgress' },
      });

    case GET_ALLOWANCE_SUCCESS: {
      const { userData } = state;
      const { payload } = action;

      const allowances = userData
        ? { ...userData.allowances, [payload.minerId]: payload }
        : {};

      return update(state, {
        getAllowanceRequest: { status: 'Success' },
        userData: userData && { ...userData, allowances },
      });
    }

    case GET_ALLOWANCE_FAILURE:
      return update(state, {
        getAllowanceRequest: {
          status: 'Error',
          error: action.payload,
        },
      });

    case AUTHENTICATE_LEDGER_REQUEST:
      return update(state, {
        authenticatingLedger: true,
        authenticatingLedgerError: '',
      });

    case AUTHENTICATE_LEDGER_SUCCESS:
      return update(state, {
        authenticatingLedger: false,
        authenticatingLedgerError: '',
      });

    case AUTHENTICATE_LEDGER_FAILURE:
      return update(state, {
        authenticatingLedger: false,
        authenticatingLedgerError: action.payload,
      });

    case WALLET_CHECK_SUPPORTED_REQUEST:
      return update(state, {
        supportedWallets: {
          ...state.supportedWallets,
          [action.walletType]: {
            ...state.supportedWallets[action.walletType],
            checking: true,
            error: '',
          },
        },
      });

    case WALLET_CHECK_SUPPORTED_SUCCESS:
      return update(state, {
        supportedWallets: {
          ...state.supportedWallets,
          [action.walletType]: {
            ...state.supportedWallets[action.walletType],
            checking: false,
            error: '',
            supported: action.payload,
          },
        },
      });

    case WALLET_CHECK_SUPPORTED_FAILURE:
      return update(state, {
        supportedWallets: {
          ...state.supportedWallets,
          [action.walletType]: {
            ...state.supportedWallets[action.walletType],
            checking: false,
            error: action.payload,
          },
        },
      });

    case UPDATE_USER_REQUEST:
      return update(state, {
        updateUserRequest: { status: 'InProgress' },
      });

    case UPDATE_USER_SUCCESS: {
      const { userData } = state;
      const { payload } = action;

      return update(state, {
        userData: userData && { ...userData, ...payload },
        updateUserRequest: { status: 'Success' },
      });
    }

    case UPDATE_USER_FAILURE:
      return update(state, {
        updateUserRequest: { status: 'Error', error: action.payload },
      });

    case CLEAR_UPDATE_USER:
      return update(state, {
        updateUserRequest: { status: 'Initial' },
      });

    case ADD_EMAIL_REQUEST: {
      return update(state, { addEmailRequest: { status: 'InProgress' } });
    }

    case ADD_EMAIL_SUCCESS: {
      const { user } = action.payload;

      return update(state, {
        addEmailRequest: { status: 'Success' },
        userData: update(state.userData, user),
      });
    }

    case ADD_EMAIL_FAILURE: {
      const { message } = action.payload;

      return update(state, { addEmailRequest: { status: 'Error', error: message } });
    }

    case SET_IS_STORAGE_PROVIDER: {
      return update(state, { isStorageProvider: action.payload });
    }
  }
};

export const UserProvider: FC = ({ children }) => {
  const { query } = useRouter();

  const [user, dispatch, getState] = useEnhancedReducer(userReducer, INITIAL_STATE);
  const userData = { user, dispatch, getState };

  useSegmentLoginTracking(user.userData);

  useEffect(() => {
    if (query.magic_credential) {
      loginViaEmailAction(query.magic_credential as string, dispatch);
    } else {
      getUserAction(dispatch);
    }
  }, [dispatch, query.magic_credential]);

  return <UserContext.Provider value={userData}>{children}</UserContext.Provider>;
};

export const useUserContext: UseUserContext = () => useContext(UserContext);

export const withUserContext: WithContext<UserContextState> =
  // eslint-disable-next-line react/display-name
  (Component, mapStateToProps) => (props) => {
    const { user, dispatch, getState } = useUserContext();
    const state = useMemo(() => mapStateToProps(user, props), [user, props]);

    return (
      <Component {...props} {...state} dispatch={dispatch} getState={getState}>
        {props.children}
      </Component>
    );
  };
